import React, { useState } from "react";
import hasPermission from "@/hooks/hasPermission";
import { Trash2, PenLine, Eye, Info, Archive, ArchiveRestore } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { noImg } from "@/assets";
import {
  AddOns,
  DetailsDialog,
  DeleteConfirmDialog,
  ImageDialog,
  ArchiveConfirmDialog,
} from "@/components/dialog/common-dialog";
import {
  deleteDishOfTheDayById,
  clearDishOfTheDayMessages,
  updateExistingDishOfTheDay,
  fetchAllDishOfTheDays,
} from "@/redux/slice/menu-manage-slice/special-menu/dish-of-the-day-slice";
import { toast } from "react-hot-toast";
import { Switch } from "@/components/ui/switch";
import { toggleItemStatus } from "@/api/new-api/staff-management/menu-manage/toggle-status";
import useAuth from "@/hooks/useAuth";

function TableData({ data }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [addOns, setAddOns] = useState(false);
  const [details, setDetails] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiveActionType, setArchiveActionType] = useState("archive");
  const [imageDialog, setImageDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const { deleteLoading, updateLoading, requestStatus, error } = useSelector(
    (state) => state.menuManage.dishTheDay
  );
  const [deleteDialog, setDeleteDialog] = useState(false);

  const closeArchiveDialog = () => {
    setArchiveDialog(false);
  };

  const archiveHandler = async (id, item, type) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearDishOfTheDayMessages());
      const newStatus = type === "archive" ? "archived" : "active";
      const dataPayload = {
        type: "update",
        item_no: item?.item_no || null,
        name: item?.name || "",
        description: item?.description || "",
        price: item?.price || null,
        day_id: item?.day?.id || null,
        vat: item?.vat ?? 0,
        status: newStatus,
        file_manager_id: item?.image?.id || undefined,
        add_ons: item?.add_ons?.map((a) => a.id) || [],
        client_id: item?.client_id,
        restaurant_id: item?.restaurant_id,
      };
      const res = await dispatch(
        updateExistingDishOfTheDay({
          id,
          data: dataPayload,
        })
      );
      if (updateExistingDishOfTheDay.fulfilled.match(res)) {
        toast.success(`Dish of the day archived successfully!`);
      } else if (updateExistingDishOfTheDay.rejected.match(res)) {
        toast.error(res?.payload || `Failed to archive Dish of the day.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      closeArchiveDialog();
    }
  };

  const deleteHandler = async (id) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearDishOfTheDayMessages());
      const res = await dispatch(deleteDishOfTheDayById(id));
      if (deleteDishOfTheDayById.fulfilled.match(res)) {
        toast.success(`Dish of the day deleted successfully!`);
      } else if (deleteDishOfTheDayById.rejected.match(res)) {
        toast.error(res?.payload || `Failed to delete Dish of the day.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      setDeleteDialog(false);
      setItemDialog(null);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await toggleItemStatus("dish-of-the-day", id);
      if (res.status === "success") {
        toast.success(res.message || "Dish of the day status updated successfully");
        dispatch(fetchAllDishOfTheDays(user?.restaurant_id));
      } else {
        toast.error(res.message || "Failed to toggle status.");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    }
  };

  return (
    <>
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead>Item Name</TableHead>
            <TableHead>Image</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Add ons</TableHead>
            <TableHead>Day</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data && data.length > 0 ? (
            data.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">{item?.name}</TableCell>
                <TableCell>
                  {" "}
                  <img
                    onClick={() => {
                      setImageDialog(true);
                      setItemDialog(item);
                    }}
                    className="h-8 rounded-md w-8 object-cover cursor-pointer"
                    src={item?.image?.file_url || noImg}
                    alt="Item Image"
                  />{" "}
                </TableCell>
                <TableCell>${item?.price || "0"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {item?.add_ons?.length}
                    <Eye
                      className="cursor-pointer"
                      onClick={() => {
                        setAddOns(true);
                        setItemDialog(item);
                      }}
                      size={18}
                    />
                  </div>
                </TableCell>
                <TableCell>{item?.day?.name || "N/A"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {item?.status === "active" ? (
                      <>
                        <span className="text-cgreen text-base">Active</span>
                        <Switch
                          checked={true}
                          onCheckedChange={() => handleToggleStatus(item.id)}
                        />
                      </>
                    ) : item?.status === "inactive" ? (
                      <>
                        <span className="text-cred text-base">Inactive</span>
                        <Switch
                          checked={false}
                          onCheckedChange={() => handleToggleStatus(item.id)}
                        />
                      </>
                    ) : item?.status === "archived" ? (
                      <span className="text-cred text-base">Archived</span>
                    ) : (
                      <span className="text-cred text-base">Draft</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right justify-end flex gap-2 items-center">
                  <div className="flex items-center gap-2 h-8">
                    <Info
                      onClick={() => {
                        setDetails(true);
                        setItemDialog(item);
                      }}
                      className=" cursor-pointer"
                      size={18}
                    />
                    {hasPermission("back_office.menu_management.update_special_menu") && item?.status !== "archived" && (
                      <Link
                        state={item}
                        to={"/backoffice/menu/special-menu/dish-the-day/update"}
                        className="cursor-pointer"
                      >
                        <PenLine className="text-cyellow" size={18} />
                      </Link>
                    )}

                    {hasPermission("back_office.menu_management.delete_special_menu") && item?.status !== "archived" && (
                      <span>
                        {item?.status === "draft" ? (
                          <Trash2
                            onClick={() => {
                              setItemDialog(item);
                              setDeleteDialog(true);
                            }}
                            className="text-cred cursor-pointer"
                            size={18}
                          />
                        ) : (
                          <Archive
                            onClick={() => {
                              setArchiveActionType("archive");
                              setItemDialog(item);
                              setArchiveDialog(true);
                            }}
                            className="text-cred cursor-pointer"
                            size={18}
                          />
                        )}
                      </span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} className="text-center">
                No data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <AddOns
        items={itemDialog}
        isOpen={addOns}
        onClose={() => setAddOns(false)}
      />

      <ImageDialog
        items={itemDialog}
        isOpen={imageDialog}
        onClose={() => setImageDialog(false)}
      />

      <DetailsDialog
        items={itemDialog}
        isOpen={details}
        onClose={() => setDetails(false)}
      />
      <ArchiveConfirmDialog
        isLoading={updateLoading}
        isOpen={archiveDialog}
        onClose={closeArchiveDialog}
        onConfirm={() => {
          archiveHandler(itemDialog.id, itemDialog, archiveActionType);
        }}
        title={archiveActionType === "archive" ? "Archive Dish of the Day?" : "Restore Dish of the Day?"}
        description={
          archiveActionType === "archive"
            ? `Are you sure you want to archive "${itemDialog?.name}"?`
            : `Are you sure you want to restore "${itemDialog?.name}"?`
        }
        isArchive={archiveActionType === "archive"}
      />
      <DeleteConfirmDialog
        isLoading={deleteLoading}
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setItemDialog(null);
        }}
        onConfirm={() => {
          deleteHandler(itemDialog.id);
        }}
        title="Delete Draft Dish of the Day?"
        description={`Are you sure you want to delete the draft dish of the day "${itemDialog?.name}"? This action cannot be undone.`}
      />
    </>
  );
}

export default TableData;
